import React from "react";
import get from "lodash/get";
import { useTranslation } from "react-i18next";

import { useGetWorkSpaceAfterSalesQuery } from "~/schemas/generated/graphql";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { getIndicatorPeriod } from "~/libs/getFormatDate";

import PanelWrapper from "~/components/PanelWrapper";
import DealerChart from "~/components/Chart";

import s from "./BlockAfterSales.less";

const BlockAfterSales = () => {
  const { t } = useTranslation();
  const currentDealerId = useCurrentDealerId();
  const { data, loading } = useGetWorkSpaceAfterSalesQuery({
    variables: {
      DealerId: currentDealerId,
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    i300 = [],
    i301 = [],
    i302 = [],
    i303 = [],
    i400 = [],
    i401 = [],
    i502 = [],
  } = get(data, "WorkSpaceGetter.getAfterSales", {});

  const ssrValues = i502
    .slice(-2)
    .map(({ doubleValue }) => +doubleValue.toFixed(1));
  const ssrLabels = i502.slice(-2).map(({ createdAt }, i, arr) => {
    const year = `${t("common.Fact")}${getIndicatorPeriod(
      { createdAt },
      "specYear"
    )}`;
    const quarter = getIndicatorPeriod({ createdAt }, "specQuarter");
    return [year, quarter];
  });

  const getIndicatorLabelsForChart = (indicators, type) =>
    indicators.slice(-2).map(({ createdAt }, i, arr) => {
      const year = `${t(`common.${type}`)}${getIndicatorPeriod(
        { createdAt },
        "specYear"
      )}`;
      const month = getIndicatorPeriod({ createdAt }, "specMonth");
      return [year, month];
    });
  const getIndicatorValuesForChart = (indicators) =>
    indicators.slice(-2).map(({ doubleValue }) => +doubleValue.toFixed(2));

  const afterSales1Values = [
    ...getIndicatorValuesForChart(i301),
    ...getIndicatorValuesForChart(i300).slice(-1),
  ];
  const afterSales2Values = [
    ...getIndicatorValuesForChart(i401),
    ...getIndicatorValuesForChart(i400).slice(-1),
  ];
  const afterSales3Values = [
    ...getIndicatorValuesForChart(i303),
    ...getIndicatorValuesForChart(i302).slice(-1),
  ];

  const afterSales1Labels = [
    ...getIndicatorLabelsForChart(i301, "Fact"),
    ...getIndicatorLabelsForChart(i300, "Plan").slice(-1),
  ];
  const afterSales2Labels = [
    ...getIndicatorLabelsForChart(i401, "Fact"),
    ...getIndicatorLabelsForChart(i400, "Plan").slice(-1),
  ];
  const afterSales3Labels = [
    ...getIndicatorLabelsForChart(i303, "Fact"),
    ...getIndicatorLabelsForChart(i302, "Plan").slice(-1),
  ];

  return (
    <PanelWrapper
      width={660}
      height={220}
      label={t("dealers.aftersales")}
      customClassName={s.after_sales_padding}
      loading={loading}
    >
      <div className={s.aftersales}>
        <div className={s.charts}>
          <div className={s.table_row}>
            <DealerChart
              values={ssrValues}
              labels={ssrLabels}
              type="ssr"
              title={t("AFTERSALES.SRR")}
              customClassName={s.chart}
            />
            <div className={s.table_cell}>
              <DealerChart
                values={afterSales1Values}
                labels={afterSales1Labels}
                type="after_sales"
                title={t("AFTERSALES.SPARE_PARTS")}
                customClassName={s.chart}
              />
            </div>
            <div className={s.table_cell}>
              <DealerChart
                values={afterSales2Values}
                labels={afterSales2Labels}
                type="after_sales"
                title={t("AFTERSALES.TM_SPARE_PARTS")}
                customClassName={s.chart}
              />
            </div>
            <div className={s.table_cell}>
              <DealerChart
                values={afterSales3Values}
                labels={afterSales3Labels}
                type="after_sales"
                title={t("AFTERSALES.ACCESSORIES")}
                customClassName={s.chart}
              />
            </div>
          </div>
        </div>
      </div>
    </PanelWrapper>
  );
};

export default BlockAfterSales;
